import { MobileID } from 'smsaero-mobileid-sdk';
import { defaults } from './defaults.js';
import { CSS }      from './styles.js';

/* ==========================================================================
 * Утилиты
 * ========================================================================== */

/** Создаёт <style> с CSS виджета и возвращает его */
function makeStyleTag() {
  var tag = document.createElement('style');
  tag.textContent = CSS;
  return tag;
}

/**
 * Глубокое слияние объектов конфига (не мутирует target).
 * null и массивы не рекурсируются.
 */
function merge(target, source) {
  var result = Object.assign({}, target);
  if (!source) return result;
  for (var key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    var val = source[key];
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      result[key] = merge(target[key] || {}, val);
    } else {
      result[key] = val;
    }
  }
  return result;
}

/** Создать DOM-элемент с классом и атрибутами */
function mkEl(tag, className, attrs) {
  var node = document.createElement(tag);
  if (className) node.className = className;
  if (attrs) {
    for (var k in attrs) {
      if (k === 'text') node.textContent = attrs[k];
      else node.setAttribute(k, attrs[k]);
    }
  }
  return node;
}

/* ==========================================================================
 * Маска телефона +7 (XXX) XXX-XX-XX
 * ========================================================================== */

/**
 * Форматирует строку цифр (начиная с 7, max 11 символов) в читаемый вид.
 * @param {string} digits
 * @returns {string}
 */
function formatPhone(digits) {
  var d = digits.replace(/^7/, ''); // убираем ведущую 7 — она идёт как «+7»
  var r = '+7';
  if (d.length === 0) return r;

  r += ' (';
  if (d.length <= 3) return r + d;

  r += d.substring(0, 3) + ') ';
  if (d.length <= 6) return r + d.substring(3);

  r += d.substring(3, 6) + '-';
  if (d.length <= 8) return r + d.substring(6);

  r += d.substring(6, 8) + '-';
  return r + d.substring(8, 10);
}

/**
 * Обработчик события input для поля телефона.
 * Применяет маску и восстанавливает позицию курсора по числу цифр.
 * @param {HTMLInputElement} input
 */
function applyPhoneMask(input) {
  var pos = input.selectionStart;
  var raw = input.value;

  // Считаем цифры до позиции курсора — по ним восстановим курсор после форматирования
  var digitsBeforeCursor = raw.substring(0, pos).replace(/\D/g, '').length;

  var digits = raw.replace(/\D/g, '');
  if (!digits) { input.value = ''; return; }

  // Нормализация: 8xxxxxxxxxx → 7xxxxxxxxxx, номер без 7 → добавляем 7
  // prefixAdded = 1 когда мы инжектировали лишнюю цифру '7' перед введёнными —
  // курсор нужно сдвинуть на 1 цифру вперёд, иначе он встанет перед '7', а не после
  var prefixAdded = 0;
  if (digits.charAt(0) === '8') {
    digits = '7' + digits.substring(1);
  } else if (digits.charAt(0) !== '7') {
    digits = '7' + digits;
    prefixAdded = 1;
  }
  digits = digits.substring(0, 11);

  var formatted = formatPhone(digits);
  input.value = formatted;

  // Восстанавливаем курсор: находим позицию после N-й цифры
  var target = digitsBeforeCursor + prefixAdded;
  var newPos;
  if (target === 0) {
    newPos = 0;
  } else {
    newPos = formatted.length; // по умолчанию — конец строки
    var count = 0;
    for (var i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        count++;
        if (count === target) { newPos = i + 1; break; }
      }
    }
  }
  input.setSelectionRange(newPos, newPos);
}

/* ==========================================================================
 * MobileIDWidget
 * ========================================================================== */

/**
 * Виджет верификации телефона через MobileID.
 *
 * @example
 * // Вариант 1: создать и смонтировать
 * const widget = new MobileIDWidget({ tokenUrl: '/api/token' });
 * widget.mount('#container');
 *
 * // Вариант 2: одной строкой
 * const widget = MobileIDWidget.init('#container', { tokenUrl: '/api/token' });
 *
 * @param {import('../src/defaults').defaults} options
 */
function MobileIDWidget(options) {
  if (!options || !options.tokenUrl) {
    throw new Error('MobileIDWidget: tokenUrl is required');
  }
  this._cfg          = merge(defaults, options);
  this._sdk          = null;
  this._host         = null;   // shadow host — <div> в DOM страницы
  this._shadow       = null;   // ShadowRoot (нужен для async-вставки customCssUrl)
  this._root         = null;   // корневой <div class="mid-widget"> внутри Shadow DOM
  this._screens      = {};     // { loading, phone, pending, otp, verified, rejected }
  this._refs         = {};     // ссылки на input, button, error-span и т.д.
  this._activeScreen = null;
  this._pendingPhone = null;   // телефон, ожидающий старта сессии
}

/* --------------------------------------------------------------------------
 * Монтирование / Уничтожение
 * ------------------------------------------------------------------------ */

/**
 * Монтирует виджет в контейнер.
 * @param {string|Element} container — CSS-селектор или DOM-элемент
 * @returns {MobileIDWidget} this (для chaining)
 */
MobileIDWidget.prototype.mount = function (container) {
  var c = typeof container === 'string'
    ? document.querySelector(container)
    : container;
  if (!c) throw new Error('MobileIDWidget: container not found: ' + container);

  // Shadow DOM полностью изолирует виджет от стилей страницы
  this._host   = mkEl('div');
  this._shadow = this._host.attachShadow({ mode: 'open' });
  this._shadow.appendChild(makeStyleTag());

  // Кастомные стили поверх базовых
  if (this._cfg.customCss) {
    var customTag = document.createElement('style');
    customTag.textContent = this._cfg.customCss;
    this._shadow.appendChild(customTag);
  }
  if (this._cfg.customCssUrl) {
    var self = this;
    fetch(this._cfg.customCssUrl)
      .then(function (r) { return r.text(); })
      .then(function (css) {
        var tag = document.createElement('style');
        tag.textContent = css;
        self._shadow.appendChild(tag);
      });
  }

  this._root = this._buildDOM();
  this._applyTheme();
  this._shadow.appendChild(this._root);

  c.appendChild(this._host);

  this._bindEvents();
  this._createSDK();

  if (this._cfg.phone) {
    // Номер передан заранее — сразу стартуем верификацию, минуя экран ввода
    this._startWithPhone(this._cfg.phone);
  } else {
    this._show('phone');
  }

  if (this._cfg.onReady) this._cfg.onReady();

  return this;
};

/**
 * Статический helper для краткой инициализации.
 * @param {string|Element} container
 * @param {Object} options
 * @returns {MobileIDWidget}
 */
MobileIDWidget.init = function (container, options) {
  return new MobileIDWidget(options).mount(container);
};

/**
 * Запустить верификацию для указанного номера телефона.
 * Можно вызвать в любой момент после mount() — например, когда номер
 * стал известен после ответа сервера.
 *
 * @param {string} phone — номер в любом формате (+7..., 8..., 9...)
 * @returns {MobileIDWidget} this
 */
MobileIDWidget.prototype.startWithPhone = function (phone) {
  this._startWithPhone(phone);
  return this;
};

/** @private */
MobileIDWidget.prototype._startWithPhone = function (phone) {
  var result = this._sdk.normalizePhone(phone);
  if (result.error) {
    this._setError('phone', this._cfg.texts.errors.invalidPhone);
    this._show('phone');
    return;
  }
  this._refs.phoneInput.value = formatPhone(result.phone); // маска для отображения
  this._refs.phoneBtn.disabled = true;
  this._pendingPhone = result.phone;
  this._show('loading');
  this._sdk.init().catch(function () { /* обрабатывается через событие 'error' */ });
};

/**
 * Возвращает введённый номер телефона в отформатированном виде (+7 (XXX) XXX-XX-XX).
 * После верификации — нормализованный номер из SDK (только цифры).
 * До ввода номера — пустая строка.
 * @returns {string}
 */
MobileIDWidget.prototype.getPhone = function () {
  if (this._sdk) {
    var sdkPhone = this._sdk.getPhone();
    if (sdkPhone) return sdkPhone;
  }
  return this._refs.phoneInput ? this._refs.phoneInput.value : '';
};

/**
 * Уничтожить виджет: остановить SDK, удалить DOM.
 */
MobileIDWidget.prototype.destroy = function () {
  if (this._sdk) { this._sdk.destroy(); this._sdk = null; }
  if (this._host && this._host.parentNode) {
    this._host.parentNode.removeChild(this._host);
  }
  this._host = null;
  this._root = null;
};

/* --------------------------------------------------------------------------
 * Построение DOM
 * ------------------------------------------------------------------------ */

MobileIDWidget.prototype._buildDOM = function () {
  var t   = this._cfg.texts;
  var inp = this._cfg.input;

  var root = mkEl('div', 'mid-widget');

  // ── Loading: показывается пока SDK инициализируется ──────────────────────
  var loading = mkEl('div', 'mid-screen mid-screen--loading');
  loading.appendChild(mkEl('div', 'mid-spinner'));
  this._screens.loading = loading;

  // ── Phone: ввод номера телефона ──────────────────────────────────────────
  var phoneScreen = mkEl('div', 'mid-screen mid-screen--phone');

  var phoneLabel = mkEl('label', 'mid-label', { text: t.phoneLabel });
  var phoneInput = mkEl('input', 'mid-input mid-input--phone', {
    type:        'tel',
    placeholder: t.phonePlaceholder,
    inputmode:   'tel',
  });
  if (inp.autocomplete) phoneInput.setAttribute('autocomplete', 'tel');

  var phoneError = mkEl('span', 'mid-error');
  var phoneBtn   = mkEl('button', 'mid-btn mid-btn--primary', { text: t.submitPhone, type: 'button' });

  phoneScreen.appendChild(phoneLabel);
  phoneScreen.appendChild(phoneInput);
  phoneScreen.appendChild(phoneError);
  phoneScreen.appendChild(phoneBtn);

  this._screens.phone   = phoneScreen;
  this._refs.phoneInput = phoneInput;
  this._refs.phoneError = phoneError;
  this._refs.phoneBtn   = phoneBtn;

  // ── Pending: ожидание ответа от внешнего API ─────────────────────────────
  var pendingScreen = mkEl('div', 'mid-screen mid-screen--pending');
  var pendingText   = mkEl('span', 'mid-pending-text', { text: t.pendingText });
  pendingScreen.appendChild(mkEl('div', 'mid-spinner'));
  pendingScreen.appendChild(pendingText);
  this._screens.pending = pendingScreen;

  // ── OTP: ввод кода из SMS ────────────────────────────────────────────────
  var otpScreen = mkEl('div', 'mid-screen mid-screen--otp');

  var otpLabel = mkEl('label', 'mid-label', { text: t.otpLabel });
  var otpInput = mkEl('input', 'mid-input mid-input--otp', {
    type:        inp.otpType,
    placeholder: t.otpPlaceholder,
    maxlength:   String(inp.otpLength),
    inputmode:   'numeric',
    pattern:     '[0-9]*',
  });
  if (inp.autocomplete) otpInput.setAttribute('autocomplete', 'one-time-code');

  var otpError = mkEl('span', 'mid-error');

  otpScreen.appendChild(otpLabel);
  otpScreen.appendChild(otpInput);
  otpScreen.appendChild(otpError);

  this._screens.otp   = otpScreen;
  this._refs.otpInput = otpInput;
  this._refs.otpError = otpError;
  this._refs.otpBtn   = null;

  if (!this._cfg.input.autoSubmitOtp) {
    var otpBtn = mkEl('button', 'mid-btn mid-btn--primary', { text: t.submitOtp, type: 'button' });
    otpScreen.appendChild(otpBtn);
    this._refs.otpBtn = otpBtn;
  }
  this._refs.backBtn  = null;

  if (this._cfg.allowChangePhone) {
    var backBtn = mkEl('button', 'mid-btn mid-btn--ghost', { text: t.back, type: 'button' });
    otpScreen.appendChild(backBtn);
    this._refs.backBtn = backBtn;
  }

  // ── Verified ─────────────────────────────────────────────────────────────
  var verified = mkEl('div', 'mid-screen mid-screen--verified');
  this._refs.verifiedInput = null;

  if (this._cfg.resultView === 'phone') {
    // Структура зеркалит экран ввода: label → input → статус ниже
    // Нет прыжков при переходе между экранами
    var verifiedLabel = mkEl('label', 'mid-label', { text: t.phoneLabel });
    var verifiedInput = mkEl('input', 'mid-input mid-input--result mid-input--result-verified', {
      type: 'tel', disabled: 'disabled',
    });
    var verifiedStatus = mkEl('span', 'mid-result-status mid-result-status--verified');
    verifiedStatus.textContent = t.successTitle;
    verified.appendChild(verifiedLabel);
    verified.appendChild(verifiedInput);
    verified.appendChild(verifiedStatus);
    this._refs.verifiedInput = verifiedInput;
  } else {
    verified.textContent = t.successTitle;
  }

  this._screens.verified = verified;

  // ── Rejected ──────────────────────────────────────────────────────────────
  var rejected = mkEl('div', 'mid-screen mid-screen--rejected');
  this._refs.rejectedInput = null;
  this._refs.retryBtn      = null;

  if (this._cfg.resultView === 'phone') {
    // Та же структура: label → input → статус ниже
    var rejectedLabel = mkEl('label', 'mid-label', { text: t.phoneLabel });
    var rejectedInput = mkEl('input', 'mid-input mid-input--result mid-input--result-rejected', {
      type: 'tel', disabled: 'disabled',
    });
    var rejectedStatus = mkEl('span', 'mid-result-status mid-result-status--rejected');
    rejectedStatus.textContent = t.rejectedTitle;
    rejected.appendChild(rejectedLabel);
    rejected.appendChild(rejectedInput);
    rejected.appendChild(rejectedStatus);
    this._refs.rejectedInput = rejectedInput;
  } else {
    var rejectedText = mkEl('div', 'mid-rejected-title');
    rejectedText.textContent = t.rejectedTitle;
    rejected.appendChild(rejectedText);
  }

  if (this._cfg.allowChangePhone) {
    var retryBtn = mkEl('button', 'mid-btn mid-btn--ghost mid-btn--retry', { text: t.retryBtn, type: 'button' });
    rejected.appendChild(retryBtn);
    this._refs.retryBtn = retryBtn;
  }

  this._screens.rejected = rejected;

  // ── Rate limit ────────────────────────────────────────────────────────────
  var rateLimit = mkEl('div', 'mid-screen mid-screen--rate-limit');
  this._refs.rateLimitInput = null;

  if (this._cfg.resultView === 'phone') {
    var rateLimitLabel = mkEl('label', 'mid-label', { text: t.phoneLabel });
    var rateLimitInput = mkEl('input', 'mid-input mid-input--result mid-input--result-rejected', {
      type: 'tel', disabled: 'disabled',
    });
    var rateLimitStatus = mkEl('span', 'mid-result-status mid-result-status--rejected');
    rateLimitStatus.textContent = t.rateLimitTitle;
    rateLimit.appendChild(rateLimitLabel);
    rateLimit.appendChild(rateLimitInput);
    rateLimit.appendChild(rateLimitStatus);
    this._refs.rateLimitInput = rateLimitInput;
  } else {
    var rateLimitText = mkEl('div', 'mid-rejected-title');
    rateLimitText.textContent = t.rateLimitTitle;
    rateLimit.appendChild(rateLimitText);
  }

  this._screens.rateLimit = rateLimit;

  // Добавляем все экраны в корень
  for (var name in this._screens) {
    root.appendChild(this._screens[name]);
  }

  return root;
};

/* --------------------------------------------------------------------------
 * Применение темы через CSS-переменные
 * ------------------------------------------------------------------------ */

MobileIDWidget.prototype._applyTheme = function () {
  var theme  = this._cfg.theme;
  var button = this._cfg.button;
  var root   = this._root;

  var vars = {
    '--mid-primary':            theme.primaryColor,
    '--mid-primary-hover':      theme.primaryHover,
    '--mid-primary-text':       theme.primaryText,
    '--mid-bg':                 theme.bgColor,
    '--mid-input-bg':           theme.inputBg,
    '--mid-input-border':       theme.inputBorder,
    '--mid-input-border-focus': theme.inputBorderFocus,
    '--mid-input-text':         theme.inputText,
    '--mid-label':              theme.labelColor,
    '--mid-hint':               theme.hintColor,
    '--mid-error':              theme.errorColor,
    '--mid-success':            theme.successColor,
    '--mid-rejected':           theme.rejectedColor,
    '--mid-spinner':            theme.spinnerColor,
    '--mid-radius':             theme.borderRadius,
    '--mid-font':               theme.fontFamily,
    '--mid-font-size':          theme.fontSize,
    '--mid-padding':            theme.padding,
    '--mid-gap-label':          theme.gapLabel,
    '--mid-gap':                theme.gap,
    '--mid-btn-width':          button.fullWidth ? '100%' : 'auto',
    '--mid-btn-radius':         button.borderRadius !== null
                                  ? button.borderRadius
                                  : theme.borderRadius,
  };

  for (var v in vars) {
    root.style.setProperty(v, vars[v]);
  }
};

/* --------------------------------------------------------------------------
 * DOM-события
 * ------------------------------------------------------------------------ */

MobileIDWidget.prototype._bindEvents = function () {
  var self = this;
  var refs = this._refs;
  var cfg  = this._cfg;

  // Маска телефона
  if (cfg.input.phoneMask) {
    refs.phoneInput.addEventListener('input', function () {
      applyPhoneMask(refs.phoneInput);
    });
  }

  // Сброс ошибок при вводе
  refs.phoneInput.addEventListener('input', function () { self._clearError('phone'); });
  refs.otpInput.addEventListener('input',   function () { self._clearError('otp'); });

  // Отправка номера
  refs.phoneBtn.addEventListener('click', function () { self._submitPhone(); });
  refs.phoneInput.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') self._submitPhone();
  });

  // Отправка OTP через кнопку / Enter (только если кнопка показана)
  if (refs.otpBtn) {
    refs.otpBtn.addEventListener('click', function () { self._submitOtp(); });
  }
  refs.otpInput.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') self._submitOtp();
  });

  // Авто-отправка при вводе последней цифры (только если включено)
  if (cfg.input.autoSubmitOtp) {
    refs.otpInput.addEventListener('input', function () {
      var digits = refs.otpInput.value.replace(/\D/g, '');
      if (digits.length >= cfg.input.otpLength) self._submitOtp();
    });
  }

  // Кнопки возврата (отсутствуют если allowChangePhone: false)
  if (refs.backBtn)  refs.backBtn.addEventListener('click',  function () { self._goBack(); });
  if (refs.retryBtn) refs.retryBtn.addEventListener('click', function () { self._goBack(); });
};

/* --------------------------------------------------------------------------
 * Действия пользователя
 * ------------------------------------------------------------------------ */

MobileIDWidget.prototype._submitPhone = function () {
  var self = this;
  var refs = this._refs;
  var cfg  = this._cfg;
  var t    = cfg.texts;

  if (refs.phoneBtn.disabled) return;

  var result = this._sdk.normalizePhone(refs.phoneInput.value);
  if (result.error) {
    this._setError('phone', t.errors.invalidPhone);
    return;
  }

  refs.phoneBtn.disabled = true;
  this._pendingPhone = result.phone;

  function proceed(captchaToken) {
    if (captchaToken) {
      self._sdk.destroy();
      self._createSDK(captchaToken);
    }
    self._show('loading');
    self._sdk.init().catch(function () { /* ошибки маршрутизируются через событие 'error' */ });
  }

  if (cfg.getCaptchaToken) {
    Promise.resolve()
      .then(function () { return cfg.getCaptchaToken(); })
      .then(function (token) { proceed(token); })
      .catch(function (err) {
        refs.phoneBtn.disabled = false;
        if (cfg.onError) cfg.onError({ code: 'CAPTCHA_ERROR', message: err && err.message || '' });
      });
  } else {
    proceed(null);
  }
};

MobileIDWidget.prototype._submitOtp = function () {
  var refs = this._refs;

  var code = refs.otpInput.value.replace(/\D/g, '');
  // otpInput.disabled — универсальный guard от двойной отправки (работает с кнопкой и без)
  if (!code || refs.otpInput.disabled) return;

  refs.otpInput.disabled = true;
  if (refs.otpBtn) refs.otpBtn.disabled = true;

  this._sdk.submitOTP(code).catch(function () {
    // Ошибки маршрутизируются через событие 'error' SDK
    if (refs.otpBtn) refs.otpBtn.disabled = false;
    refs.otpInput.disabled = false;
  });
};

MobileIDWidget.prototype._goBack = function () {
  var refs = this._refs;

  refs.otpInput.value = '';
  this._clearError('otp');

  // Уничтожаем текущую сессию и создаём новый экземпляр SDK без init —
  // следующая сессия стартует только когда пользователь снова нажмёт кнопку
  this._sdk.destroy();
  this._createSDK();

  refs.phoneBtn.disabled = false;
  this._show('phone');
};

/* --------------------------------------------------------------------------
 * Инициализация SDK и обработка событий
 * ------------------------------------------------------------------------ */

MobileIDWidget.prototype._createSDK = function (captchaToken) {
  var self = this;
  var cfg  = this._cfg;
  var t    = cfg.texts;
  var refs = this._refs;

  var tokenUrl = cfg.tokenUrl;
  if (captchaToken) {
    tokenUrl += (tokenUrl.indexOf('?') === -1 ? '?' : '&')
      + 'captcha_token=' + encodeURIComponent(captchaToken);
  }

  var sdkOpts = { tokenUrl: tokenUrl, country: cfg.country };
  if (cfg.baseUrl) sdkOpts.baseUrl = cfg.baseUrl;

  this._sdk = new MobileID(sdkOpts);

  this._sdk
    // Сессия создана — запускаем верификацию с сохранённым номером
    .on('ready', function () {
      var phone = self._pendingPhone;
      self._pendingPhone = null;
      if (phone) {
        self._show('pending');
        self._sdk.start(phone).catch(function () {
          refs.phoneBtn.disabled = false;
          self._show('phone');
        });
      } else {
        refs.phoneBtn.disabled = false;
        self._show('phone');
      }
    })

    // Запрос отправлен (в т.ч. после синхронизации) — экран ожидания
    .on('pending', function () {
      self._show('pending');
    })

    // Требуется OTP
    .on('otp_required', function () {
      refs.otpInput.value    = '';
      refs.otpInput.disabled = false;
      if (refs.otpBtn) refs.otpBtn.disabled = false;
      self._clearError('otp');
      self._show('otp');
      setTimeout(function () { refs.otpInput.focus(); }, 50);
    })

    // Верификация пройдена
    .on('verified', function (data) {
      if (refs.verifiedInput) {
        refs.verifiedInput.value = refs.phoneInput.value || formatPhone(self._sdk.getPhone());
      }
      self._show('verified');
      if (cfg.onVerified) {
        cfg.onVerified({
          verify_token: data.verify_token,
          phone:        self._sdk.getPhone(),
          session_id:   self._sdk.getSessionId(),
        });
      }
    })

    // Верификация отклонена
    .on('rejected', function (data) {
      if (refs.rejectedInput) {
        refs.rejectedInput.value = refs.phoneInput.value || formatPhone(self._sdk.getPhone());
      }
      self._show('rejected');
      if (cfg.onRejected) {
        cfg.onRejected({
          verify_token: data.verify_token,
          session_id:   self._sdk.getSessionId(),
        });
      }
    })

    // Превышен лимит запросов — показываем финальный экран без возможности повтора
    .on('rate_limit', function () {
      if (refs.rateLimitInput) {
        refs.rateLimitInput.value = refs.phoneInput.value || formatPhone(self._sdk.getPhone());
      }
      self._show('rateLimit');
      if (cfg.onRateLimit) cfg.onRateLimit();
    })

    // Сессия на стороне внешнего API истекла
    .on('expired', function () {
      refs.otpInput.disabled = false;
      if (refs.otpBtn) refs.otpBtn.disabled = false;
      self._setError('otp', t.errors.sessionExpired);
      self._show('otp');
    })

    // Неверный OTP-код
    .on('invalid_code', function () {
      refs.otpInput.value    = '';
      refs.otpInput.disabled = false;
      if (refs.otpBtn) refs.otpBtn.disabled = false;
      self._setError('otp', t.errors.invalidCode);
      setTimeout(function () { refs.otpInput.focus(); }, 50);
    })

    // Ошибка
    .on('error', function (err) {
      var msg = t.errors.unknown;
      if (err.code === 'INVALID_PHONE') msg = t.errors.invalidPhone;
      else if (err.status === 0)        msg = t.errors.networkError;

      if (cfg.onError) cfg.onError(err);

      if (self._activeScreen === 'otp') {
        refs.otpInput.disabled = false;
        if (refs.otpBtn) refs.otpBtn.disabled = false;
        self._setError('otp', msg);
      } else {
        refs.phoneBtn.disabled = false;
        self._setError('phone', msg);
        self._show('phone');
      }
    });

  // init() не вызывается здесь — сессия стартует в _submitPhone()
};

/* --------------------------------------------------------------------------
 * Вспомогательные методы
 * ------------------------------------------------------------------------ */

/** Показать экран по имени, скрыть остальные */
MobileIDWidget.prototype._show = function (screen) {
  this._activeScreen = screen;
  for (var name in this._screens) {
    var el = this._screens[name];
    if (name === screen) el.classList.add('mid-screen--active');
    else                 el.classList.remove('mid-screen--active');
  }
};

/** Показать ошибку под полем ввода */
MobileIDWidget.prototype._setError = function (scope, msg) {
  var errEl   = scope === 'phone' ? this._refs.phoneError : this._refs.otpError;
  var inputEl = scope === 'phone' ? this._refs.phoneInput : this._refs.otpInput;
  errEl.textContent = msg || '';
  if (msg) inputEl.classList.add('mid-input--error');
  else     inputEl.classList.remove('mid-input--error');
};

/** Очистить ошибку */
MobileIDWidget.prototype._clearError = function (scope) {
  this._setError(scope, '');
};

export { MobileIDWidget };
export default MobileIDWidget;
